import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRegisterDataIntegrationStepControls } from 'src/features/Signup/hooks/useRegisterDataIntegrationStepControls';
import { PreparingYourDataStep } from 'src/features/Signup/components/templates/DataIntegrationFlow/steps/PreparingYourDataStep';
import {
  getStepIndex,
  getNextStepPath,
  getPrevStepPath,
} from 'src/features/Signup/utils/dataIntegrationSteps';
import { useDataConnectorJobsQuery } from 'src/api/queries';
import { useAppContext } from 'src/contexts/AppContext';

const nextPath = getNextStepPath('preparing-data');
const prevPath = getPrevStepPath('preparing-data');

export const PreparingYourDataStepRoute: React.FC = () => {
  const navigate = useNavigate();
  const { currentCustomer } = useAppContext();

  const { data: jobsData } = useDataConnectorJobsQuery(
    { customer: currentCustomer?.customerName ?? '', job_type: 'initial' },
    !!currentCustomer?.customerName,
    5000,
  );

  const allFetchesComplete = useMemo(() => {
    if (!jobsData || jobsData.length === 0) return true;
    return jobsData.every(
      (job) =>
        job.status === 'complete' ||
        job.status === 'error' ||
        job.status === 'no_tasks',
    );
  }, [jobsData]);

  const failedServices = useMemo(() => {
    if (!jobsData) return [];
    return jobsData
      .filter((job) => job.status === 'error')
      .map((job) => job.service.replace('_', ' '));
  }, [jobsData]);

  const hasNoConnections = !jobsData || jobsData.length === 0;

  const getSubtitle = () => {
    if (hasNoConnections) {
      return "No channels connected yet. It's not too late to connect your platforms or skip this step if you uploaded data manually.";
    }
    if (!allFetchesComplete) {
      return "We're syncing your connected channels. This may take a few moments.";
    }
    if (failedServices.length > 0) {
      return `Some channels failed to sync (${failedServices.join(', ')}). You can proceed with the available data or try to reconnect.`;
    }
    return 'Your data is ready. You can proceed to the next step.';
  };

  useRegisterDataIntegrationStepControls({
    title: 'Preparing your data',
    subtitle: getSubtitle(),
    currentStep: getStepIndex('preparing-data'),
    onNext: nextPath ? () => navigate(nextPath) : undefined,
    onBack: prevPath ? () => navigate(prevPath) : undefined,
    isBackDisabled: false,
    isNextDisabled: !allFetchesComplete,
  });

  return <PreparingYourDataStep />;
};
